import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { SPECTRUM_COLORS } from '../SpectrumMaterial';

const SpectrumLineClearEffect = ({ rows, linesCleared, onComplete }) => {
  const groupRef = useRef();
  const flashRefs = useRef([]);
  const sparkleRefs = useRef([]);
  const startTime = useRef(Date.now());

  // Generate sparkle positions along the lines
  const sparkles = useMemo(() => {
    const data = [];
    const sparklePerLine = 15;

    rows.forEach((row) => {
      const baseY = 20 - row - 0.5;
      for (let i = 0; i < sparklePerLine; i++) {
        data.push({
          x: (i / sparklePerLine) * 10 - 5 + (Math.random() - 0.5) * 0.5,
          y: baseY + (Math.random() - 0.5) * 0.3,
          z: 0.3 + Math.random() * 0.2,
          delay: i * 0.02 + Math.random() * 0.05,
          size: 0.08 + Math.random() * 0.06,
          speed: 1 + Math.random() * 0.5,
        });
      }
    });
    return data;
  }, [rows]);

  useFrame(() => {
    const elapsed = (Date.now() - startTime.current) / 1000;
    const duration = 0.6;

    if (elapsed > duration) {
      onComplete?.();
      return;
    }

    const progress = elapsed / duration;

    // Animate flash bars - sweep from left to right then fade
    flashRefs.current.forEach((flash, i) => {
      if (!flash) return;
      const sweepProgress = Math.min(1, elapsed * 4); // Fast sweep
      flash.scale.x = sweepProgress;
      flash.position.x = -5 + sweepProgress * 5;
      flash.material.opacity = (1 - progress) * 0.7;
    });

    // Animate sparkles - pop up and fade
    sparkleRefs.current.forEach((sparkle, i) => {
      if (!sparkle) return;
      const data = sparkles[i];
      const localProgress = Math.max(0, (elapsed - data.delay) / (duration - data.delay));

      if (localProgress > 0 && localProgress < 1) {
        sparkle.visible = true;
        // Pop up and float
        sparkle.position.y = data.y + localProgress * 0.8;
        sparkle.position.z = data.z + localProgress * 0.3;
        // Scale up then down
        const scaleProgress = localProgress < 0.3 ? localProgress / 0.3 : 1 - (localProgress - 0.3) / 0.7;
        sparkle.scale.setScalar(data.size * scaleProgress * 3);
        sparkle.material.opacity = (1 - localProgress) * 0.9;
      } else {
        sparkle.visible = false;
      }
    });
  });

  const accentColor = new THREE.Color(SPECTRUM_COLORS.accent);
  const whiteColor = new THREE.Color(0xffffff);

  return (
    <group ref={groupRef}>
      {/* Sweeping flash bars */}
      {rows.map((row, i) => (
        <mesh
          key={`flash-${i}`}
          ref={(el) => (flashRefs.current[i] = el)}
          position={[-5, 20 - row - 0.5, 0.15]}
        >
          <planeGeometry args={[10, 0.9]} />
          <meshBasicMaterial
            color={accentColor}
            transparent
            opacity={0.7}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* White center line */}
      {rows.map((row, i) => (
        <mesh
          key={`center-${i}`}
          position={[0, 20 - row - 0.5, 0.2]}
        >
          <planeGeometry args={[10, 0.15]} />
          <meshBasicMaterial
            color={whiteColor}
            transparent
            opacity={0.9}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}

      {/* Sparkles */}
      {sparkles.map((data, i) => (
        <mesh
          key={`sparkle-${i}`}
          ref={(el) => (sparkleRefs.current[i] = el)}
          position={[data.x, data.y, data.z]}
          visible={false}
        >
          <circleGeometry args={[1, 6]} />
          <meshBasicMaterial
            color={i % 3 === 0 ? whiteColor : accentColor}
            transparent
            opacity={0.9}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </mesh>
      ))}
    </group>
  );
};

export default SpectrumLineClearEffect;
